import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { isLocalhost, sanitizeDifficultyRatings } from "../Config/config";
import useEscapeRoomApi from "../Hooks/useEscapeRoomApi";
import { DifficultyRatings } from "../Types/Game";

/**
 * DifficultyRatingsContext
 * ------------------------
 * Global room difficulty overrides, shared by every event (kept out of
 * GameContext on purpose — see CLAUDE.md). Fetched once signed in, cached in
 * localStorage so the lobby shows the right badges straight away on reload,
 * and falls back to the built-in defaults if the API is unavailable.
 */

const STORAGE_KEY = "difficultyRatings";

type DifficultyRatingsContextValue = {
  ratings: DifficultyRatings;
  /** Persist new overrides for all events. Throws if the save fails. */
  saveRatings: (next: DifficultyRatings) => Promise<void>;
};

export const DifficultyRatingsContext = createContext<DifficultyRatingsContextValue>({
  ratings: {},
  saveRatings: async () => {},
});

const readCachedRatings = (): DifficultyRatings => {
  try {
    return sanitizeDifficultyRatings(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"));
  } catch {
    return {};
  }
};

export const DifficultyRatingsProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const { getDifficultyRatings, saveDifficultyRatings } = useEscapeRoomApi();
  const [ratings, setRatings] = useState<DifficultyRatings>(readCachedRatings);

  const applyRatings = (next: DifficultyRatings) => {
    setRatings(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  // The API needs a token outside localhost, so wait until signed in
  const canFetch = isLocalhost || auth.isAuthenticated;
  useEffect(() => {
    if (!canFetch) return;
    getDifficultyRatings()
      .then((raw) => applyRatings(sanitizeDifficultyRatings(raw)))
      .catch(() => {
        // Keep the cached/default ratings — badges are informational only
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch]); // getDifficultyRatings is recreated every render

  const saveRatings = async (next: DifficultyRatings) => {
    const clean = sanitizeDifficultyRatings(next);
    await saveDifficultyRatings(clean);
    applyRatings(clean);
  };

  return (
    <DifficultyRatingsContext.Provider value={{ ratings, saveRatings }}>
      {children}
    </DifficultyRatingsContext.Provider>
  );
};

export const useDifficultyRatings = () => useContext(DifficultyRatingsContext);
